import React, { useState } from "react";
import { Alert, Button, Modal } from "react-bootstrap";
import { Link } from "react-router-dom";
import ReviewHelp from "./ReviewHelp";
import { customerWorkflow, customerFollowUpExpired } from "./customerWorkflow";
import {
  dateLabel,
  dueLabel,
  followUpEnded,
  isOverdue,
  nextActionFor,
  reviewApi,
  reviewNotStarted,
} from "./api";
import FollowUpSummary from "./FollowUpSummary";
export default function ReviewLifecycle({
  review: r,
  attorney,
  onChanged,
  onError,
  clientSeq,
  attorneySeq,
  compact = false,
  actions,
  customerActions,
  now = Date.now(),
}: {
  review: any;
  attorney: boolean;
  onChanged: () => Promise<void>;
  onError: (e: any) => void;
  clientSeq: number;
  attorneySeq: number;
  compact?: boolean;
  /** Attorney only: callbacks that turn this banner into the review's action bar. */
  actions?: {
    openTerms?: () => void;
    start?: () => void;
    deliver: () => void;
    canDeliver: boolean;
    showMessages: () => void;
  };
  customerActions?: {
    proposal: () => void;
    discussion: () => void;
    answers: () => void;
  };
  now?: number;
}) {
  const [intent, setIntent] = useState<any>(null),
    [busy, setBusy] = useState(false);
  const confirm = intent?.kind;
  const setConfirm = (kind: string | null) =>
    setIntent(
      kind ? { kind, version: r.version, clientSeq, attorneySeq } : null
    );
  const stale =
    !!intent &&
    (intent.version !== r.version ||
      intent.clientSeq !== clientSeq ||
      intent.attorneySeq !== attorneySeq);
  const ended = !!r.closed_at || r.status === "cancelled",
    delivered = r.status === "completed",
    awaitingPayment =
      r.proposal_state === "agreed" && r.payment_state === "awaiting_payment";
  const needsReply =
    Number(r.latest_client_seq) > Number(r.attorney_replied_through);
  const roundsLeft = Math.max(
    0,
    Number(r.clarification_round_limit ?? 0) -
      Number(r.clarification_rounds_used ?? 0)
  );
  const overdue = isOverdue(r, now);
  // Attorney copy speaks to the attorney; the client copy below is unchanged.
  const next = nextActionFor(r),
    legacy = !r.proposal_state || r.proposal_state === "legacy";
  const dueText = r.due_at
    ? overdue
      ? `Overdue since ${dateLabel(r.due_at)}`
      : `Due ${dueLabel(r.due_at, now)}`
    : "";
  const attorneyHeading =
    next === "reply"
      ? delivered
        ? "The client sent a follow-up. Reply to them."
        : "The client replied. Respond before you deliver."
      : next === "waiting_on_client"
        ? "Waiting for the client to reply. You can keep drafting."
        : delivered
          ? followUpEnded(r, now)
            ? "Follow-up window ended. Close the review."
            : "Answer delivered"
          : r.proposal_state === "awaiting_client"
            ? "You proposed changes. Waiting for the client to approve."
            : awaitingPayment
              ? "Accepted. Waiting for the client's payment."
              : r.status === "assigned"
                ? legacy
                  ? "New review. Start when you are ready."
                  : "New review. Accept the terms or propose changes."
                : dueText || "Write and deliver your answer";
  const attorneyExplanation =
    (next === "reply" || next === "waiting_on_client") && !delivered && dueText
      ? `${dueText}. ${overdue ? "Message the client about the delay and any documents still needed. " : ""}The deadline does not pause while you wait for the client.`
      : delivered
        ? followUpEnded(r, now)
          ? `The follow-up window ended ${dateLabel(r.clarification_deadline)}. Revisions and document requests are closed; more work needs a new review.`
          : r.clarification_round_limit != null
            ? "Handle clarification within the agreed allowance. Revisions and document requests remain available until the follow-up window ends. Close the review when the agreed work is finished."
            : "Follow-up terms remain as originally agreed. When the work is finished, close the review."
        : r.proposal_state === "awaiting_client"
          ? "The client must approve your revised terms before work begins. You can explain them in the discussion."
          : awaitingPayment
            ? "Both sides agreed. Work and the deadline start when the client pays by card."
            : r.status === "assigned"
              ? "Accept the client-approved terms to start, or propose changes for the client to approve. The deadline starts only once both sides agree and payment clears."
              : r.due_at
                ? `${overdue ? "Your answer is overdue. Deliver it as soon as possible, or message the client about the delay." : `Deliver your answer by ${dateLabel(r.due_at)}.`} The deadline does not pause while you wait for the client. Request documents allowed by the review scope in Messages.`
                : "No deadline was agreed for this earlier review. Discuss any documents needed for the existing question in Messages.";
  async function act() {
    setBusy(true);
    try {
      await reviewApi(`/${r.id}/${confirm}`, "POST", {
        version: intent.version,
        clientSeq: intent.clientSeq,
        attorneySeq: intent.attorneySeq,
      });
      setConfirm(null);
      await onChanged();
    } catch (e) {
      onError(e);
    } finally {
      setBusy(false);
    }
  }
  const confirmModal = (
    <Modal show={!!confirm} onHide={() => !busy && setConfirm(null)} centered>
      <Modal.Header closeButton={!busy}>
        <Modal.Title>
          {confirm === "resolve"
            ? "Resolve this discussion?"
            : confirm === "withdraw"
              ? "Cancel this request?"
              : "Close this review?"}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {stale && (
          <Alert variant="warning">
            New activity arrived while this confirmation was open. Close this
            dialog and read the latest discussion before continuing.
          </Alert>
        )}
        {confirm === "resolve"
          ? "This marks the client messages you have seen as resolved without sending a reply. Use it only when no response is needed. New messages will still need your attention."
          : confirm === "withdraw"
            ? "This ends the request before work starts. Nothing is charged, your question stays available, and you can request a review again later."
            : "Confirm that the agreed work is finished and no further response is needed. This ends discussion and drafting on this review. Its record and answers stay available; additional work needs a new request."}
        {confirm === "resolve" &&
          delivered &&
          r.clarification_round_limit != null && (
            <p className="mt-3 mb-0">
              This ends the current clarification round. It does not restore a
              used round.
            </p>
          )}
        {confirm === "close" &&
          delivered &&
          r.clarification_deadline &&
          !followUpEnded(r) && (
            <Alert variant="warning" className="mt-3">
              The follow-up window is still open until{" "}
              {dateLabel(r.clarification_deadline)}. Closing ends discussion and
              drafting immediately.
            </Alert>
          )}
        {confirm === "close" && needsReply && (
          <Alert variant="warning" className="mt-3">
            You have an outstanding message. Closing confirms that you no longer
            need a reply.
          </Alert>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button
          variant="light"
          disabled={busy}
          onClick={() => setConfirm(null)}
        >
          {confirm === "withdraw" ? "Keep request" : "Keep open"}
        </Button>
        <Button disabled={busy || stale} onClick={act}>
          {busy
            ? "Saving…"
            : confirm === "withdraw"
              ? "Cancel request"
              : confirm === "close" && !attorney
                ? "Close review and end messaging"
                : "Confirm"}
        </Button>
      </Modal.Footer>
    </Modal>
  );
  if (!attorney) {
    const state = customerWorkflow(r, now);
    const followUpPending =
      Number(r.latest_client_seq || 0) >
      Number(r.attorney_replied_through || 0);
    const followUpExpired = customerFollowUpExpired(r, now);
    const followUpText = ended
      ? "Messaging ended. Your answer and messages remain available."
      : followUpPending
        ? followUpExpired
          ? "Your follow-up was sent. The window to add messages has ended. Read your messages for any reply."
          : "Your follow-up is awaiting a reply. Messages you add before the attorney replies count as the same follow-up."
        : followUpExpired
          ? "The agreed follow-up window has ended. Additional work needs a new review."
          : roundsLeft === 0
            ? "No included follow-ups remain. You can close this review or request another review."
            : `You have ${roundsLeft} included follow-up${roundsLeft === 1 ? "" : "s"} remaining${r.clarification_deadline ? ` until ${dateLabel(r.clarification_deadline)}` : ""}. A follow-up includes your messages until the attorney replies. New questions need a new review.`;
    return (
      <section
        className={`customer-next-step${state.needsAction ? " needs-action" : ""}`}
        aria-label="What to do next"
      >
        <div>
          <span className="customer-next-step-label">
            {state.needsAction ? "YOUR NEXT STEP" : "REVIEW STATUS"}
          </span>
          <h2>{state.heading}</h2>
          <p>{state.explanation}</p>
          {!ended && r.status === "in_progress" && r.due_at && (
            <p>
              <strong>Answer due {dueLabel(r.due_at, now)}</strong>
            </p>
          )}
          {state.key === "matching" && (
            <p>Matching time varies. There is no fixed matching deadline.</p>
          )}
          {delivered &&
            !ended &&
            r.clarification_deadline &&
            (followUpExpired || roundsLeft > 0 || followUpPending) && (
              <p className="customer-follow-up-deadline">
                <strong>
                  {followUpExpired
                    ? `Follow-up window ended ${dateLabel(r.clarification_deadline)}`
                    : `${followUpPending ? "You can add to this follow-up" : "Included follow-up available"} until ${dueLabel(r.clarification_deadline, now)}`}
                </strong>
              </p>
            )}
          {delivered && r.clarification_round_limit != null && (
            <details className="customer-follow-up">
              <summary>
                {ended
                  ? "Follow-up history"
                  : followUpPending
                    ? "Follow-up sent — waiting for reply"
                    : followUpExpired
                      ? "Follow-up window ended"
                      : roundsLeft
                        ? `${roundsLeft} included follow-up${roundsLeft === 1 ? "" : "s"} available`
                        : "Included follow-up completed"}
              </summary>
              <p>{followUpText}</p>
            </details>
          )}
        </div>
        <div className="review-lifecycle-actions">
          {state.action && customerActions && (
            <Button onClick={customerActions[state.action]}>
              {state.actionLabel}
            </Button>
          )}
          {!ended &&
            delivered &&
            state.key === "answered" &&
            customerActions && (
              <Button
                variant="outline-dark"
                onClick={customerActions.discussion}
              >
                Ask a follow-up
              </Button>
            )}
          {!ended && delivered && (
            <Button variant="outline-dark" onClick={() => setConfirm("close")}>
              Close review
            </Button>
          )}
          {!ended && reviewNotStarted(r) && (
            <Button variant="link" onClick={() => setConfirm("withdraw")}>
              Cancel request
            </Button>
          )}
          {(delivered || ended) && (
            <Link to={`/questions/${r.question_id}?requestReview=1`}>
              Request another review
            </Link>
          )}
        </div>
        <ReviewHelp reviewId={r.id} />
        {confirmModal}
      </section>
    );
  }
  if (attorney && actions) {
    const primary = (
      label: string,
      onClick: () => void,
      disabled = false,
      title?: string
    ) => (
      <Button onClick={onClick} disabled={disabled} title={title}>
        {label}
      </Button>
    );
    const secondary = (label: string, onClick: () => void) => (
      <Button variant="outline-dark" onClick={onClick}>
        {label}
      </Button>
    );
    const deliver = primary(
      "Deliver answer",
      actions.deliver,
      !actions.canDeliver,
      actions.canDeliver
        ? undefined
        : "Write and save your answer before delivering"
    );
    return (
      <section
        className={`review-action-bar is-${overdue && next === "draft" ? "overdue" : next}`}
        aria-label="What to do next"
      >
        <div className="review-action-text">
          <strong>
            {ended
              ? r.status === "cancelled"
                ? "Request ended"
                : "Review closed"
              : attorneyHeading}
          </strong>
          <span>
            {ended
              ? `${r.closure_reason || "Closed"}${r.closed_at ? ` · ${dateLabel(r.closed_at)}` : ""}. The record and delivered answers remain available.`
              : attorneyExplanation}
          </span>
          {!ended && delivered && <FollowUpSummary review={r} />}
        </div>
        <div className="review-lifecycle-actions">
          {!ended &&
            next === "accept" &&
            (legacy
              ? actions.start && primary("Start review", actions.start)
              : actions.openTerms &&
                primary("Review and accept", actions.openTerms))}
          {!ended && next === "reply" && (
            <>
              {primary("Reply to client", actions.showMessages)}
              {secondary("No reply needed", () => setConfirm("resolve"))}
            </>
          )}
          {!ended && next === "draft" && (
            <>
              {deliver}
              {secondary("Message client", actions.showMessages)}
            </>
          )}
          {!ended && next === "waiting_on_client" && (
            <>
              {deliver}
              {secondary("View messages", actions.showMessages)}
            </>
          )}
          {!ended &&
            next === "follow_up_open" &&
            primary("Close review", () => setConfirm("close"))}
          {actions.openTerms && !legacy && next !== "accept" && (
            <Button variant="link" onClick={actions.openTerms}>
              {next === "waiting_approval" ? "View your proposal" : "Terms"}
            </Button>
          )}
        </div>
        {confirmModal}
      </section>
    );
  }
  return (
    <section
      className={`review-lifecycle${compact ? " is-compact" : ""}`}
      aria-label="Review next steps"
    >
      <div>
        <strong>
          {ended
            ? r.status === "cancelled"
              ? "Request ended"
              : "Review closed"
            : attorneyHeading}
        </strong>
        <details
          className="lifecycle-explanation"
          open={compact ? undefined : ended}
        >
          <summary>
            {delivered && r.clarification_round_limit != null
              ? customerFollowUpExpired(r, now)
                ? `Client follow-up window ended ${dateLabel(r.clarification_deadline)}`
                : `Client follow-up: ${roundsLeft} left${r.clarification_deadline ? `, until ${dateLabel(r.clarification_deadline)}` : ""}`
              : "How this stage works"}
          </summary>
          {ended ? (
            <p>
              {r.closure_reason}. {r.closed_at && dateLabel(r.closed_at)}. Your
              submitted record and delivered answers remain available.
            </p>
          ) : (
            <p>{attorneyExplanation}</p>
          )}
        </details>
      </div>
      <div className="review-lifecycle-actions">
        {!ended && needsReply && (
          <Button variant="outline-dark" onClick={() => setConfirm("resolve")}>
            Resolve discussion
          </Button>
        )}
        {!ended && delivered && !needsReply && (
          <Button variant="outline-dark" onClick={() => setConfirm("close")}>
            Close review
          </Button>
        )}
      </div>
      {confirmModal}
    </section>
  );
}
